<template>
  <div class="my-repair-dispatch-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('myRepairDispatchManage.page.title')" :subtitle="$t('myRepairDispatchManage.page.subtitle')" />

    <!-- (2) SearchPanel -->
    <ds-search-panel>
      <el-row :gutter="16">
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-select v-model="myRepairDispatchInfo.conditions.maintenanceType" clearable class="w-full"
            :placeholder="$t('myRepairDispatchManage.selectMaintenanceType')">
            <el-option v-for="(item, index) in myRepairDispatchInfo.maintenanceTypes" :key="index" :label="item.name"
              :value="item.statusCd" />
          </el-select>
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="myRepairDispatchInfo.conditions.repairName" clearable class="w-full"
            :placeholder="$t('myRepairDispatchManage.inputRepairName')" @keyup.enter.native="_queryMyRepairDispatchMethod" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="myRepairDispatchInfo.conditions.tel" clearable class="w-full"
            :placeholder="$t('myRepairDispatchManage.inputRepairTel')" @keyup.enter.native="_queryMyRepairDispatchMethod" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-select v-model="myRepairDispatchInfo.conditions.repairType" clearable class="w-full"
            :placeholder="$t('myRepairDispatchManage.selectRepairType')">
            <el-option v-for="(item, index) in myRepairDispatchInfo.repairTypes" :key="index" :label="item.repairTypeName"
              :value="item.repairType" />
          </el-select>
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-select v-model="myRepairDispatchInfo.conditions.state" clearable class="w-full"
            :placeholder="$t('myRepairDispatchManage.selectRepairState')">
            <el-option v-for="(item, index) in myRepairDispatchInfo.states" :key="index" :label="item.name"
              :value="item.statusCd" />
          </el-select>
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="myRepairDispatchInfo.conditions.repairId" clearable class="w-full"
            :placeholder="$t('myRepairDispatchManage.inputRepairId')" @keyup.enter.native="_queryMyRepairDispatchMethod" />
        </el-col>
      </el-row>

      <template #actions>
        <el-button type="primary" icon="el-icon-search" @click="_queryMyRepairDispatchMethod">
          {{ $t('common.search') }}
        </el-button>
        <el-button icon="el-icon-refresh-left" @click="_resetMyRepairDispatchMethod">
          {{ $t('common.reset') }}
        </el-button>
      </template>
    </ds-search-panel>

    <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar :total="pagination.total" :density="density" :show-column-setting="false" :show-refresh="true"
        @update:density="density = $event" @refresh="_queryMyRepairDispatchMethod">
        <template #left>
          <span class="ds-panel__title">{{ $t('myRepairDispatchManage.repairDispatch') }}</span>
          <span v-if="myRepairDispatchInfo.conditions.roomName" class="ds-text-muted">
            · {{ myRepairDispatchInfo.conditions.roomName }}
          </span>
        </template>
      </ds-toolbar>

      <ds-data-table :data="myRepairDispatchInfo.ownerRepairs" :loading="loading" :error="hasError"
        :error-message="errorMessage" :density="density" border :empty-description="$t('myRepairDispatchManage.page.empty')"
        @retry="_queryMyRepairDispatchMethod">
        <el-table-column prop="repairId" :label="$t('myRepairDispatchManage.repairId')" align="center" width="150">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.repairId || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="repairObjName" :label="$t('myRepairDispatchManage.location')" align="center" min-width="150"
          show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.repairObjName || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="repairTypeName" :label="$t('myRepairDispatchManage.repairType')" align="center" width="130">
          <template slot-scope="scope">
            {{ scope.row.repairTypeName || '-' }}
          </template>
        </el-table-column>
        <el-table-column :label="$t('myRepairDispatchManage.maintenanceType')" align="center" width="120">
          <template slot-scope="scope">
            {{ getMaintenanceTypeName(scope.row.maintenanceType) }}
          </template>
        </el-table-column>
        <el-table-column prop="repairName" :label="$t('myRepairDispatchManage.repairPerson')" align="center" width="110">
          <template slot-scope="scope">
            {{ scope.row.repairName || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="tel" :label="$t('myRepairDispatchManage.contact')" align="center" width="130">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.tel || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="appointmentTime" :label="$t('myRepairDispatchManage.appointmentTime')" align="center"
          width="170">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.appointmentTime || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('myRepairDispatchManage.state')" align="center" width="140">
          <template slot-scope="scope">
            <ds-status-tag :label="scope.row.stateName || '-'" :text="scope.row.stateName"
              :type="getStateType(scope.row.state)" />
          </template>
        </el-table-column>
        <el-table-column :label="$t('common.operation')" align="center" width="100" fixed="right">
          <template slot-scope="scope">
            <el-button size="mini" type="primary" @click="_openRepairDetail(scope.row)">
              {{ $t('common.detail') }}
            </el-button>
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar :total="pagination.total" :page="pagination.current" :size="pagination.size"
        :page-sizes="[10, 20, 30, 50]" @size-change="handleSizeChange" @current-change="handleCurrentChange" />
    </div>

    <close-order ref="closeOrderModal" @success="handleCloseOrderSuccess" />
  </div>
</template>

<script>
import { listStaffFinishRepairs, listRepairSettings, closeRepairDispatch } from '@/api/work/myRepairDispatchManageApi'
import { getDict } from '@/api/community/communityApi'
import CloseOrder from '@/components/work/closeOrder'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'

export default {
  name: 'MyRepairDispatchManageList',
  components: {
    CloseOrder,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsPaginationBar,
    DsStatusTag
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      myRepairDispatchInfo: {
        ownerRepairs: [],
        moreCondition: false,
        currentRepairId: '',
        repairTypes: [],
        states: [],
        maintenanceTypes: [],
        conditions: {
          pageFlag: 'myRepairDispatch',
          repairId: '',
          repairName: '',
          tel: '',
          repairType: '',
          roomId: '',
          roomName: '',
          ownerId: '',
          state: '',
          maintenanceType: '',
          page: 1,
          row: 10,
          communityId: ''
        }
      },
      pagination: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  created() {
    this.initData()
  },
  methods: {
    async initData() {
      try {
        this.myRepairDispatchInfo.conditions.communityId = this.getCommunityId()
        await this.getDictData()
        this._listRepairTypes()
        this._listOwnerRepairs(1, this.pagination.size)
      } catch (error) {
        console.error('初始化数据失败:', error)
      }
    },

    async getDictData() {
      try {
        const stateData = await getDict('r_repair_pool', 'state')
        this.myRepairDispatchInfo.states = stateData

        const maintenanceTypeData = await getDict('r_repair_pool', 'maintenance_type')
        this.myRepairDispatchInfo.maintenanceTypes = maintenanceTypeData
      } catch (error) {
        console.error('获取字典数据失败:', error)
      }
    },

    async _listOwnerRepairs(page, size) {
      this.loading = true
      this.hasError = false
      this.errorMessage = ''
      try {
        this.myRepairDispatchInfo.conditions.page = page
        this.myRepairDispatchInfo.conditions.row = size

        const params = {
          ...this.myRepairDispatchInfo.conditions,
          repairId: this.myRepairDispatchInfo.conditions.repairId.trim(),
          repairName: this.myRepairDispatchInfo.conditions.repairName.trim(),
          tel: this.myRepairDispatchInfo.conditions.tel.trim()
        }

        const res = await listStaffFinishRepairs(params)
        this.myRepairDispatchInfo.ownerRepairs = res.data
        this.pagination.total = res.total
        this.pagination.current = page
      } catch (error) {
        this.hasError = true
        this.errorMessage = this.$t('myRepairDispatchManage.page.fetchError')
        console.error('获取报修列表失败:', error)
      } finally {
        this.loading = false
      }
    },

    async _listRepairTypes() {
      try {
        const params = {
          page: 1,
          row: 100,
          communityId: this.getCommunityId()
        }

        const res = await listRepairSettings(params)
        this.myRepairDispatchInfo.repairTypes = res.data
      } catch (error) {
        console.error('获取报修类型失败:', error)
      }
    },

    _moreCondition() {
      this.myRepairDispatchInfo.moreCondition = !this.myRepairDispatchInfo.moreCondition
    },

    _openRepairDetail(repairPool) {
      this.$router.push({
        path: '/views/work/repairDetail',
        query: { repairId: repairPool.repairId }
      })
    },

    _openDealRepair(ownerRepair) {
      this.myRepairDispatchInfo.currentRepairId = ownerRepair.repairId
      this.$refs.closeOrderModal.open()
    },

    async handleCloseOrderSuccess(orderInfo) {
      try {
        const params = {
          repairId: this.myRepairDispatchInfo.currentRepairId,
          context: orderInfo.remark,
          communityId: this.getCommunityId(),
          state: orderInfo.state === '1100' ? '10002' : '10003'
        }

        await closeRepairDispatch(params)
        this.$message.success(this.$t('common.operationSuccess'))
        this._listOwnerRepairs(this.pagination.current, this.pagination.size)
      } catch (error) {
        console.error('结单操作失败:', error)
      }
    },

    _queryMyRepairDispatchMethod() {
      this._listOwnerRepairs(1, this.pagination.size)
    },

    _resetMyRepairDispatchMethod() {
      this.myRepairDispatchInfo.conditions = {
        ...this.myRepairDispatchInfo.conditions,
        repairId: '',
        repairName: '',
        tel: '',
        repairType: '',
        state: '',
        maintenanceType: ''
      }
      this._listOwnerRepairs(1, this.pagination.size)
    },

    getMaintenanceTypeName(type) {
      const mapping = {
        '1001': this.$t('myRepairDispatchManage.paidService'),
        '1002': this.$t('myRepairDispatchManage.freeService'),
        '1003': this.$t('myRepairDispatchManage.needMaterial'),
        '1004': this.$t('myRepairDispatchManage.noMaterial')
      }
      return mapping[type] || '--'
    },

    getStateType(state) {
      switch (state) {
        case '1000': return 'info'
        case '1800': return 'warning'
        case '1900': return 'success'
        case '2001': return 'muted'
        default: return 'info'
      }
    },

    handleSizeChange(size) {
      this.pagination.size = size
      this._listOwnerRepairs(1, size)
    },

    handleCurrentChange(page) {
      this._listOwnerRepairs(page, this.pagination.size)
    }
  }
}
</script>

<style lang="scss" scoped>
.my-repair-dispatch-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 面板标题 */
.ds-panel__title {
  font-size: var(--ds-font-size-lg);
  font-weight: 600;
  color: var(--ds-gray-800);
}
</style>
